/**
 * ============================================================================
 * COMPONENTE: SidebarUserPhoto
 * ============================================================================
 * Descrição: Exibe a foto do usuário logado no sidebar (user-panel)
 * Usa o hook useOraclePhoto para buscar a URL assinada do Oracle Storage
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';
import api from '../services/api';
import { oracleUrlCache } from '../services/oracleUrlCache';

interface SidebarUserPhotoProps {
  className?: string;
  style?: React.CSSProperties;
}

const SidebarUserPhoto: React.FC<SidebarUserPhotoProps> = ({ 
  className = 'img-circle elevation-2',
  style = {} 
}) => {
  const usuario = authService.getUsuario();
  const [fotoUrl, setFotoUrl] = useState<string>('/img/avatar.svg');
  const [loading, setLoading] = useState(true);
  
  // Buscar foto do usuário
  useEffect(() => {
    const usuarioId = usuario?.id
      ? (typeof usuario.id === 'string' ? parseInt(usuario.id) : usuario.id)
      : null;

    const fetchFoto = async () => {
      if (!usuarioId) {
        setFotoUrl('/img/avatar.svg');
        setLoading(false);
        return;
      }
      
      // Verificar cache primeiro
      const cachedUrl = oracleUrlCache.get('usuario_foto', usuarioId);
      if (cachedUrl) {
        // Se cache é fallback/avatar, forçar nova consulta para recuperar foto recente.
        const isFallbackAvatar = cachedUrl.includes('/img/avatar.svg');
        if (!isFallbackAvatar) {
          setFotoUrl(cachedUrl);
          setLoading(false);
          return;
        }

        oracleUrlCache.remove('usuario_foto', usuarioId);
      }
      
      try {
        const token = sessionStorage.getItem('token');
        if (!token) {
          setFotoUrl('/img/avatar.svg');
          setLoading(false);
          return;
        }
        
        const response = await api.get(`/usuarios/${usuarioId}/foto`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const { url: signedUrl, tipo, expiraEm } = response.data;
        
        if (signedUrl) {
          oracleUrlCache.set('usuario_foto', usuarioId, signedUrl, tipo, expiraEm);
          setFotoUrl(signedUrl);
        }
      } catch (err) {
        // Silenciosamente usar avatar padrão
        setFotoUrl('/img/avatar.svg');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFoto();

    const onUserPhotoUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ usuarioId?: number }>;
      const updatedUserId = Number(customEvent.detail?.usuarioId);
      if (updatedUserId && updatedUserId === usuarioId) {
        oracleUrlCache.remove('usuario_foto', usuarioId);
        setLoading(true);
        fetchFoto();
      }
    };

    window.addEventListener('userPhotoUpdated', onUserPhotoUpdated as EventListener);

    return () => {
      window.removeEventListener('userPhotoUpdated', onUserPhotoUpdated as EventListener);
    };
  }, [usuario?.id]);

  // Se está carregando, mostrar placeholder animado
  if (loading) {
    return (
      <div 
        className={`${className} bg-secondary d-flex align-items-center justify-content-center`}
        style={{ 
          width: '34px', 
          height: '34px',
          ...style 
        }}
      >
        <i className="fas fa-user text-white fa-sm"></i>
      </div>
    );
  }

  // Renderizar imagem
  return (
    <img 
      src={fotoUrl}
      alt={usuario?.nome || 'Usuário'}
      className={className}
      style={{ 
        width: '34px', 
        height: '34px', 
        objectFit: 'cover',
        ...style 
      }}
      onError={(e) => {
        // Fallback em caso de erro
        const imgElement = e.target as HTMLImageElement;
        imgElement.src = '/img/avatar.svg';
        imgElement.onerror = null; // Prevenir loops
      }}
    />
  );
};

export default SidebarUserPhoto;
